import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ConfirmResetPasswordRequestDTO } from 'src/app/shared/models/dtos/requests/confirm-reset-password-request-dto/confirm-reset-password-request-dto';
import { LoginRequestDTO } from 'src/app/shared/models/dtos/requests/login-request-dto/login-request-dto';
import { ResetPasswordRequestDTO } from 'src/app/shared/models/dtos/requests/reset-password-request-dto/reset-password-request-dto';
import { LoginResponseDTO } from 'src/app/shared/models/dtos/responses/login-response-dto/login-response-dto';
import { Account } from 'src/app/shared/models/dtos/account/account';

import { BusinessResponseDTO } from 'src/app/shared/models/dtos/register-form/business-response-dto/business-response-dto';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { HashValidationDTO } from 'src/app/shared/models/dtos/responses/hash-validation-dto/hash-validation-dto';
import { InviteRepresentativeRequestDTO } from 'src/app/shared/models/dtos/requests/invite-representative-request-dto/invite-representative-request-dto';
import { InviteRepresentativeResponseDTO } from 'src/app/shared/models/dtos/responses/invite-representative-response-dto/invite-representative-response-dto';
import { RefreshSessionResponseDTO } from 'src/app/shared/models/dtos/responses/refresh-session-response-dto/refresh-session-response-dto';
import { SignUpResponseDTO } from 'src/app/shared/models/dtos/responses/sign-up-response-dto/sign-up-response-dto';
import { GenericResponseDTO } from 'src/app/shared/models/dtos/responses/message-response-dto/message-response-dto';
import { BusinessDTO } from 'src/app/shared/models/dtos/requests/business-dto/business-dto';
import { Country } from 'src/app/shared/models/dtos/register-form/country/country';
import { RepresentativeUpdateRequestDTO } from 'src/app/shared/models/dtos/requests/representative-update-request-dto/representative-update-request-dto';
import { ComplianceUrlsResponseDTO } from 'dtos/responses/compliance-urls-response-dto';
import { ChangeTemporaryPasswordRequestDTO } from 'src/app/shared/models/dtos/requests/change-temporary-password-request-dto';
import { CouponVip } from 'src/app/shared/models/dtos/responses/coupon-vip/coupon-vip';
import { BannerResponseDTO } from 'src/app/shared/models/dtos/responses/banner-response-dto/banner-response-dto';

@Injectable({ providedIn: 'root' })
/**
 * Service to interact with LnR's User Management Service
 */
export class UserService {
  private backendURL = '/ums/api';
  private backendAuthURL = '/ums/api/a';

  /**
   * Injects HttpClient to interact with LnR Backend
   * @constructor
   */
  constructor(private httpClient: HttpClient) {}

  /**
   * Retrieves authentication token from backend
   * @param {LoginRequestDTO} loginRequestDTO
   * @return {Observable<LoginResponseDTO>}
   */
  getAuthenticationToken(
    loginRequestDTO: LoginRequestDTO
  ): Observable<LoginResponseDTO> {
    const endpoint = 'login';
    const body = loginRequestDTO.serialize();

    return this.httpClient.post<LoginRequestDTO>(this.buildURL(endpoint), body);
  }

  /**
   * Registers a Business
   * @param {Account} account
   * @param {string} recaptcha
   * @return {Observable<SignUpResponseDTO>}
   */
  registerBusiness(
    account: Account,
    recaptcha: string
  ): Observable<SignUpResponseDTO> {
    const endpoint = 'account/create';
    return this.httpClient.post<SignUpResponseDTO>(this.buildURL(endpoint), {
      ...account,
      recaptchaResponse: recaptcha,
    });
  }

  /**
   * Registers a representative
   * @param {Representative} representative
   * @param {string} recaptcha
   * @return {Observable<SignUpResponseDTO>}
   */
  registerRepresentative(
    representative: Representative,
    recaptcha: string
  ): Observable<SignUpResponseDTO> {
    const endpoint = 'representative/invite/accept/new';
    return this.httpClient.post<SignUpResponseDTO>(this.buildURL(endpoint), {
      ...representative,
      recaptchaResponse: recaptcha,
    });
  }

  /**
   * User verification
   * @param {string} username
   * @param {string} code
   * @return {Observable<any>}
   */
  verifyCode(username: string, code: string): Observable<any> {
    const endpoint = 'account/confirm-account';

    return this.httpClient.post(this.buildURL(endpoint), {
      username,
      code,
    });
  }

  /**
   * Resend user verification code
   * @param {string} username
   * @return {Observable<any>}
   */
  resendCode(username: string): Observable<any> {
    const endpoint = 'account/resend-code';

    return this.httpClient.post(this.buildURL(endpoint), { username });
  }

  /**
   * @param {ResetPasswordRequestDTO} resetPasswordRequestDTO
   * @return {Observable<any>}
   */
  resetPassword(
    resetPasswordRequestDTO: ResetPasswordRequestDTO
  ): Observable<any> {
    const endpoint = 'user/password/reset';
    const body = resetPasswordRequestDTO.serialize();

    return this.httpClient.post(this.buildURL(endpoint), body);
  }

  /**
   * @param {ConfirmResetPasswordRequestDTO} confirmResetPasswordRequestDTO
   * @return {Observable<any>}
   */
  confirmResetPassword(
    confirmResetPasswordRequestDTO: ConfirmResetPasswordRequestDTO
  ): Observable<any> {
    const endpoint = 'user/password/reset/confirm';
    const body = confirmResetPasswordRequestDTO.serialize();
    return this.httpClient.post(this.buildURL(endpoint), body);
  }

  /**
   * @param {BusinessResponseDTO} business
   * @return {Observable<BusinessResponseDTO>}
   */
  updateBusinessData(business: BusinessDTO): Observable<BusinessDTO> {
    const endpoint = 'business/update';
    const body = business.serialize();
    return this.httpClient.put<BusinessDTO>(this.buildAuthURL(endpoint), body);
  }

  /**
   * @return {Observable<Representative>}
   */
  getRepresentativeInfo(): Observable<Representative> {
    const endpoint = 'representative/info';
    return this.httpClient.get<Representative>(this.buildAuthURL(endpoint));
  }

  /**
   * @return {Observable<Representative[]>}
   */
  listRepresentatives(): Observable<Representative[]> {
    const endpoint = 'representative/list-from-business';
    return this.httpClient.get<Representative[]>(this.buildAuthURL(endpoint));
  }

  /**
   * @param {number} businessId
   * @return {Observable<InviteRepresentativeResponseDTO[]>}
   */
  listInvitees(): Observable<InviteRepresentativeResponseDTO[]> {
    const endpoint = 'invite/list-from-business';
    return this.httpClient.get<InviteRepresentativeResponseDTO[]>(
      this.buildAuthURL(endpoint)
    );
  }

  /**
   * @param {number} representativeId
   * @return {Observable<number>}
   */
  promoteRepresentative(representativeId: number): Observable<number> {
    const endpoint = `representative/promote/${representativeId}`;
    return this.httpClient
      .get<GenericResponseDTO>(this.buildAuthURL(endpoint))
      .pipe(map((response) => parseInt(response.message)));
  }

  /**
   * @param {number} representativeId
   * @return {Observable<number>}
   */
  deleteRepresentative(representativeId: number): Observable<number> {
    const endpoint = `representative/${representativeId}/disable`;
    return this.httpClient
      .delete<GenericResponseDTO>(this.buildAuthURL(endpoint))
      .pipe(map((response) => parseInt(response.message)));
  }

  /**
   * @return {Observable<BusinessResponseDTO>}
   */
  getBusinessInfo(): Observable<BusinessResponseDTO> {
    const endpoint = 'business/info';
    return this.httpClient.get<BusinessResponseDTO>(
      this.buildAuthURL(endpoint)
    );
  }

  /**
   * @param {InviteRepresentativeRequestDTO} inviteRepresentativeRequestDTO
   * @return {Observable<string>}
   */
  inviteRepresentative(
    inviteRepresentativeRequestDTO: InviteRepresentativeRequestDTO
  ): Observable<string> {
    const endpoint = 'representative/invite';
    return this.httpClient
      .post<GenericResponseDTO>(
        this.buildAuthURL(endpoint),
        inviteRepresentativeRequestDTO
      )
      .pipe(map((response) => response.message));
  }

  /**
   * @param {number} id
   * @return {Observable<number>}
   */
  deleteInvite(id: number): Observable<number> {
    const endpoint = `invite/${id}`;
    return this.httpClient
      .delete<GenericResponseDTO>(this.buildAuthURL(endpoint))
      .pipe(map((response) => parseInt(response.message)));
  }

  /**
   * @param {string} hash to validate
   * @return {Observable<HashValidationDTO>}
   */
  validateHash(hash: string): Observable<HashValidationDTO> {
    const endpoint = `invite/validate?hash=${hash}`;
    return this.httpClient.get<HashValidationDTO>(this.buildURL(endpoint));
  }

  /**
   * @param {string} email
   * @return {Observable<Representative>}
   */
  existingRepresentativeAcceptInvite(
    email: string
  ): Observable<Representative> {
    const endpoint = `representative/invite/accept/${email}`;
    return this.httpClient.put<Representative>(this.buildAuthURL(endpoint), {
      email,
    });
  }

  /**
   * @param {string} refreshToken
   * @return {Observable<any>}
   */
  logout(refreshToken: string): Observable<any> {
    const endpoint = 'logout';
    return this.httpClient.post(this.buildURL(endpoint), {
      refreshToken,
    });
  }

  /**
   * @param {string} refreshToken
   * @return {Observable<RefreshSessionResponseDTO>}
   */
  refreshSession(refreshToken: string): Observable<RefreshSessionResponseDTO> {
    const endpoint = 'refreshSession';
    return this.httpClient.post<RefreshSessionResponseDTO>(
      this.buildURL(endpoint),
      {
        refreshToken,
      }
    );
  }

  /**
   * Returns a list of countries
   * @return {Observable<Country[]>}
   */
  listCountries(): Observable<Country[]> {
    const endpoint = 'region/detailed';
    return this.httpClient.get<Country[]>(this.buildURL(endpoint));
  }

  /**
   * Validates a business contact email
   * @param {string} businessContactEmail the email to validate
   * @return {Observable<boolean>} if it is valid or not
   */
  validateBusinessEmail(businessContactEmail: string): Observable<boolean> {
    const endpoint = `business/check/email/${businessContactEmail}`;
    return this.httpClient
      .get<GenericResponseDTO>(this.buildURL(endpoint))
      .pipe(
        map((response) => {
          return response.message.toLowerCase() === 'false';
        })
      );
  }

  /**
   * Validates a business contact email using authenticated endpoint
   * @param {string} businessContactEmail the email to validate
   * @return {Observable<string>} if it is valid or not
   */
  validateBusinessEmailAuth(businessContactEmail: string): Observable<string> {
    const endpoint = `business/check/email/${businessContactEmail}`;
    return this.httpClient
      .get<GenericResponseDTO>(this.buildAuthURL(endpoint))
      .pipe(map((response) => response.message));
  }

  /**
   * Changes a representative's password
   * @param {string} currentPassword
   * @param {string} newPassword
   * @return {Observable<any>}
   */
  changePassword(
    currentPassword: string,
    newPassword: string
  ): Observable<any> {
    const endpoint = 'user/password/change';
    return this.httpClient.put(this.buildAuthURL(endpoint), {
      currentPassword,
      newPassword,
    });
  }

  /**
   * Updates representative data
   * @param {RepresentativeUpdateRequestDTO} representative
   * @param {string} recaptcha
   * @return {Observable<Representative>}
   */
  updateRepresentative(
    representative?: RepresentativeUpdateRequestDTO,
    recaptcha?: string
  ): Observable<Representative> {
    const endpoint = 'representative';
    return this.httpClient.put<Representative>(this.buildAuthURL(endpoint), {
      ...representative,
      recaptchaResponse: recaptcha,
    });
  }

  /**
   * Updates representative language
   * @param {any} language
   * @return {Observable<Representative>}
   */
  updateRepresentativeLanguage(language: any): Observable<Representative> {
    const endpoint = 'representative/language';
    return this.httpClient.put<Representative>(
      this.buildAuthURL(endpoint),
      language
    );
  }

  /**
   * Disable representative
   * @param {LoginRequestDTO} loginRequestDTO
   * @return {Observable<GenericResponseDTO>}
   */
  disableRepresentative(
    loginRequestDTO: LoginRequestDTO
  ): Observable<GenericResponseDTO> {
    const endpoint = 'representative/disable';
    return this.httpClient.delete<GenericResponseDTO>(
      this.buildAuthURL(endpoint),
      {
        body: loginRequestDTO,
      }
    );
  }

  /**
   * @return {Observable<ComplianceUrlsResponseDTO>}
   */
  getTermsOfUseAndPrivacyConditionsURLs(): Observable<ComplianceUrlsResponseDTO> {
    const endpoint = 'compliance/terms-and-privacy';
    return this.httpClient.get<ComplianceUrlsResponseDTO>(
      this.buildURL(endpoint)
    );
  }

  /**
   * @param {ChangeTemporaryPasswordRequestDTO} request
   * @return {Observable<string>}
   */
  changeTemporaryPassword(
    request: ChangeTemporaryPasswordRequestDTO
  ): Observable<LoginResponseDTO> {
    const endpoint = 'user/password/temporary/change';
    return this.httpClient.post<LoginResponseDTO>(
      this.buildURL(endpoint),
      request
    );
  }

  /**
   * @return {Observable<CouponVip[]>}
   */
  getCouponsVip(): Observable<CouponVip[]> {
    const endpoint = 'business_vip';
    return this.httpClient
      .get<CouponVip[]>(this.buildAuthURL(endpoint))
      .pipe(map((coupons) => coupons));
  }

  /**
   * @param {number} couponId
   * @return {Observable<CouponVip>}
   */
  getCouponVip(couponId: number): Observable<CouponVip> {
    const endpoint = `business_vip/${couponId}`;
    return this.httpClient
      .get<CouponVip>(this.buildAuthURL(endpoint))
      .pipe(map((coupon) => coupon));
  }

  /**
   * @return {Observable<BusinessResponseDTO>}
   */
  checkNewBusiness(): Observable<BusinessResponseDTO> {
    const endpoint = 'business/new-business-check';
    return this.httpClient.get<BusinessResponseDTO>(
      this.buildAuthURL(endpoint)
    );
  }

  /**
   * @param {number} businessId
   * @return {Observable<any>}
   */
  getBusinessUnificationStatus(businessId: number): Observable<any> {
    const endpoint = `business-unification/business/${businessId}`;
    return this.httpClient.get<any>(this.buildAuthURL(endpoint));
  }

  /**
   * @param {number} businessId
   * @return {Observable<any>}
   */
  marksBusinessAsNotifiedAboutUnification(businessId: number): Observable<any> {
    const endpoint = `business-unification/marks-as-notified/${businessId}`;
    return this.httpClient.post<any>(this.buildAuthURL(endpoint), {});
  }

  /**
   * Gets banner information
   * @param {string} lang
   * @return {Observable<BannerResponseDTO>}
   */
  getBannerInfo(lang: string): Observable<BannerResponseDTO> {
    const endpoint: string = 'banner';
    const params: any = {
      language: lang,
    };
    return this.httpClient.get<BannerResponseDTO>(this.buildAuthURL(endpoint), {
      params,
    });
  }

  /**
   * Builds URL from endpoint
   * @param {string} endpoint
   * @return {string}
   */
  private buildURL(endpoint: string): string {
    return `${this.backendURL}/${endpoint}`;
  }

  /**
   * Builds URL from endpoint with authentication
   * @param {string} endpoint
   * @return {string}
   */
  private buildAuthURL(endpoint: string): string {
    return `${this.backendAuthURL}/${endpoint}`;
  }

  /**
   * Gets a user service API version
   * @return {Observable<string>}
   */
  getApiVersion(): Observable<string> {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    return this.httpClient.get(`${this.backendURL}/version`, {
      headers,
      responseType: 'text',
    });
  }
}
